import { Injectable, inject, ApplicationRef, NgZone, signal } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Router, NavigationStart } from '@angular/router';
import { MessageService } from 'primeng/api';
import { filter, concatMap, first, from, of, tap, timer, timeout, fromEvent, merge, Subscription } from 'rxjs';
import { debounceTime, startWith } from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
import { environment } from '@/environments/environment';
import { compareSemver } from '@shared/utils/version.utils';
import { isPackagedApp } from '@shared/utils/platform.utils';

/** Cada cuánto se consulta al servidor si hay una versión nueva (detección periódica). */
const CHECK_INTERVAL_MINUTES = 5;
/**
 * Respaldo: si el usuario se queda en la MISMA pantalla (no cambia de ruta ni de pestaña), tras este
 * lapso de inactividad real se aplica la actualización y se recarga.
 */
const INACTIVITY_TIMEOUT_MINUTES = 2;
/** Tiempo máximo que esperamos a que la app esté "estable" antes de arrancar el polling igualmente. */
const STABILITY_TIMEOUT_MS = 30_000;
/** Anti-rebote para los chequeos disparados por foco/visibilidad (evita ráfagas al alternar pestañas). */
const FOCUS_CHECK_DEBOUNCE_MS = 2_000;
/**
 * Aplicar automáticamente la versión nueva cuando la pestaña pasa a segundo plano (recarga invisible).
 * Es el momento más cómodo para el usuario, pero implica una recarga: si tuviera un formulario a medio
 * llenar y cambia de pestaña, perdería lo no guardado. Poner en `false` para desactivar esta vía
 * (quedarían: cambio de ruta, inactividad y el botón "Actualizar ahora").
 */
const AUTO_RELOAD_ON_HIDDEN = true;
/** Clave en `sessionStorage` con el timestamp de la última recarga automática por actualización. */
const SW_RELOAD_FLAG = 'erp:sw-reload-at';
/** Ventana de guarda anti-bucle: si tras recargar se vuelve a intentar dentro de este lapso, no se recarga otra vez. */
const SW_RELOAD_GUARD_MS = 30_000;
/** Clave en `sessionStorage` con el timestamp de la última "carga limpia" (desregistro de SW + borrado de cachés). */
const CLEAN_RELOAD_FLAG = 'erp:sw-clean-reload-at';
/** Ventana de guarda anti-bucle para la carga limpia (más amplia: es una operación cara). */
const CLEAN_RELOAD_GUARD_MS = 60_000;
/**
 * Ventana tras el arranque durante la cual, si llega una versión nueva, se aplica **de inmediato**
 * (es el momento más seguro: el usuario recién abrió, no hay datos a medio escribir). Cumple el
 * objetivo "al abrir, siempre fresca" sin recarga limpia: basta activar la versión ya descargada.
 */
const BOOT_APPLY_WINDOW_MS = 90_000;
/** Chequeos consecutivos con desfase requeridos (fuera del arranque) antes de forzar carga limpia. */
const VERSION_GATE_MISMATCH_THRESHOLD = 3;

@Injectable({
  providedIn: 'root',
})
export class UpdateService {
  private swUpdate = inject(SwUpdate);
  private messageService = inject(MessageService);
  private appRef = inject(ApplicationRef);
  private document = inject(DOCUMENT);
  private ngZone = inject(NgZone);
  private router = inject(Router);

  private inactivitySubscription?: Subscription;
  private navigationSubscription?: Subscription;

  /** Hay una versión nueva ya descargada y lista para activar en esta sesión de pestaña. */
  private readonly updateReady = signal(false);

  /** Marca de tiempo del arranque de la app (creación del servicio singleton). Ver `BOOT_APPLY_WINDOW_MS`. */
  private bootAt = Date.now();

  /**
   * Válvula para que pantallas críticas (p. ej. un formulario con cambios sin guardar) puedan
   * **vetar temporalmente** la auto-recarga silenciosa. Mientras esté en `false`, las vías
   * automáticas (cambio de ruta, segundo plano, inactividad) NO recargan; el usuario conserva el
   * botón "Actualizar ahora" y, al volver a `true`, la próxima vía segura aplicará la actualización.
   */
  readonly autoReloadAllowed = signal(true);

  /**
   * Indica que ya se notificó al usuario de una actualización disponible en esta sesión de pestaña.
   * Evita acumular/repetir el toast si llegan varios `VERSION_READY` (p. ej. varios deploys mientras
   * la pestaña sigue abierta). No se resetea: al aplicar la actualización la app se recarga, lo que
   * crea una instancia nueva del servicio con el flag en `false`.
   */
  private updatePrompted = false;

  constructor() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((evt) => {
        switch (evt.type) {
          case 'VERSION_DETECTED':
            console.log(`SW: Downloading new app version: ${evt.version.hash}`);
            break;
          case 'VERSION_READY':
            console.log(`SW: Current app version: ${evt.currentVersion.hash}`);
            console.log(`SW: New app version ready for use: ${evt.latestVersion.hash}`);
            break;
          case 'VERSION_INSTALLATION_FAILED':
            // La instalación de la versión nueva falló (típico de deploys encadenados: el chunk que
            // intentaba bajar ya fue reemplazado). No interrumpimos al usuario: el próximo chequeo
            // periódico/por-foco volverá a intentarlo con el manifiesto más reciente.
            console.warn(
              `SW: Failed to install app version '${evt.version.hash}': ${evt.error}. Se reintentará en el próximo chequeo.`
            );
            break;
        }
      });

      // Estado irrecuperable: el SW ya no garantiza consistencia y la app suele estar rota. Una
      // recarga normal podría volver a topar con el mismo SW roto, así que forzamos una CARGA LIMPIA
      // (desregistro + borrado de cachés + recarga) — equivale a lo que ve un dispositivo nuevo.
      this.swUpdate.unrecoverable.subscribe((event) => {
        console.warn(`SW: Unrecoverable state: ${event.reason}. Forzando carga limpia.`);
        void this.forceCleanReload();
      });
    }
  }

  public init() {
    if (!this.swUpdate.isEnabled) {
      console.log('SW: Service Worker updates are disabled');
      return;
    }

    console.log('SW: Service Worker updates are enabled');

    // Confirmar que el registro a nivel de navegador fue exitoso
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then((registration) => {
          console.log('SW: Service Worker is ready and controlling the app', registration);
        })
        .catch((err) => {
          console.error('SW: Error waiting for Service Worker readiness', err);
        });
    }

    // Esperar a que la aplicación sea estable antes de iniciar el polling
    const appIsStable$ = this.appRef.isStable.pipe(
      first((isStable) => isStable === true),
      timeout({
        first: STABILITY_TIMEOUT_MS,
        with: () => {
          console.warn(`SW: App did not stabilize in ${STABILITY_TIMEOUT_MS / 1000}s, proceeding anyway`);
          return of(true);
        },
      })
    );

    const checkInterval$ = timer(0, CHECK_INTERVAL_MINUTES * 60 * 1000);

    appIsStable$
      .pipe(
        // Esperar también a que el SW esté listo y activo para evitar errores en checkForUpdate
        concatMap(() => ('serviceWorker' in navigator ? from(navigator.serviceWorker.ready) : of(null))),
        tap(() => console.log('SW: App is stable and SW is ready. Starting polling...')),
        concatMap(() => checkInterval$)
      )
      .subscribe(() => {
        console.log('SW: Checking for update...');
        this.checkForUpdate();
        void this.checkVersionGate();
      });

    // Detección dirigida por eventos: cuando el usuario vuelve a la pestaña/app re-chequeamos al
    // instante (no esperamos al siguiente tick del polling). Esto ataca directamente el caso
    // "cerró y volvió a abrir": al recuperar el foco detectamos la versión nueva enseguida.
    this.setupFocusDrivenChecks();

    // Manejar cuando una nueva versión está lista
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        console.log('SW: New version ready event received');
        this.onVersionReady();
      });
  }

  /** Chequeo de actualización seguro (no rompe si el SW aún no está listo). */
  private checkForUpdate(): void {
    this.swUpdate
      .checkForUpdate()
      .then((hasUpdate) => {
        if (hasUpdate) console.log('SW: Update found during check');
      })
      .catch((err) => {
        console.error('SW: Error checking for update', err);
        console.log(`SW: Will retry on next interval/focus`);
      });
  }

  /** Chequeos consecutivos que vieron un bundle más viejo que el desplegado. */
  private gateMismatches = 0;

  /**
   * GATE de versión mínima: compara la versión desplegada (/version.json, fuera del alcance del
   * SW y pedido sin caché) con la del bundle en ejecución. Es el techo duro al desfase: actúa
   * aunque el service worker esté roto o nunca haya tomado control. Al arrancar el desfase se
   * corrige de inmediato (momento seguro); en medio de la sesión se exige que persista
   * VERSION_GATE_MISMATCH_THRESHOLD chequeos para no pisar el flujo suave del SW tras un deploy.
   */
  public async checkVersionGate(): Promise<void> {
    if (isPackagedApp()) return; // Tauri se actualiza por auto-updater; no aplica
    try {
      const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const remote = (await res.json()) as { version?: string };
      if (!remote.version || compareSemver(remote.version, environment.version) <= 0) {
        this.gateMismatches = 0;
        return;
      }

      if (Date.now() - this.bootAt < BOOT_APPLY_WINDOW_MS) {
        console.warn(`GATE: bundle ${environment.version} < desplegado ${remote.version} al arrancar; carga limpia.`);
        void this.forceCleanReload();
        return;
      }

      this.gateMismatches++;
      if (this.gateMismatches >= VERSION_GATE_MISMATCH_THRESHOLD && this.autoReloadAllowed()) {
        console.warn(`GATE: desfase persistente tras ${this.gateMismatches} chequeos; carga limpia.`);
        this.gateMismatches = 0;
        void this.forceCleanReload();
      }
    } catch {
      // Sin red o CDN caído: nada que hacer; el próximo chequeo reintenta.
    }
  }

  /**
   * Re-chequea actualizaciones cuando la pestaña vuelve a primer plano y, si hay una versión lista,
   * la aplica cuando la pestaña pasa a segundo plano (recarga invisible). Corre fuera de la zona de
   * Angular para no disparar change detection con estos eventos del documento.
   */
  private setupFocusDrivenChecks(): void {
    this.ngZone.runOutsideAngular(() => {
      const visibility$ = fromEvent(this.document, 'visibilitychange');

      // Volvió a primer plano → detectar enseguida (con anti-rebote para no spamear al alternar).
      merge(visibility$, fromEvent(window, 'focus'))
        .pipe(debounceTime(FOCUS_CHECK_DEBOUNCE_MS))
        .subscribe(() => {
          if (this.document.visibilityState === 'visible') {
            this.checkForUpdate();
          }
        });

      // Pasó a segundo plano y ya hay versión lista → aplicar de forma invisible (sin anti-rebote:
      // debe ocurrir mientras está oculta). Respeta la válvula `autoReloadAllowed` y el flag global.
      visibility$.subscribe(() => {
        if (
          AUTO_RELOAD_ON_HIDDEN &&
          this.document.visibilityState === 'hidden' &&
          this.updateReady() &&
          this.autoReloadAllowed()
        ) {
          this.ngZone.run(() => this.applyUpdate());
        }
      });
    });
  }

  /** Hay una versión nueva lista: marcamos el estado, avisamos una vez y armamos las vías de auto-aplicación. */
  private onVersionReady(): void {
    this.updateReady.set(true);

    // En la ventana de arranque es seguro aplicar de una (el usuario recién abrió, no hay datos a medio
    // escribir): así "al abrir, siempre fresca" con la vía barata (activar la versión ya descargada),
    // sin recarga limpia. Pasada la ventana, se difiere a un momento seguro (ruta/segundo plano/inactividad).
    if (this.autoReloadAllowed() && Date.now() - this.bootAt < BOOT_APPLY_WINDOW_MS) {
      console.log('SW: Versión nueva durante el arranque; aplicando de inmediato.');
      this.applyUpdate();
      return;
    }

    this.promptUpdate();
    this.applyOnNextNavigation();
    this.startInactivityTracker();
  }

  private promptUpdate() {
    // Una sola notificación por sesión de pestaña: si ya avisamos no volvemos a lanzar otro toast
    // aunque llegue otro VERSION_READY (varios deploys con la pestaña abierta).
    if (this.updatePrompted) {
      console.log('SW: Actualización ya notificada en esta sesión; se omite un nuevo aviso.');
      return;
    }
    this.updatePrompted = true;

    this.messageService.add({
      key: 'update-toast',
      severity: 'info',
      summary: 'Actualización disponible',
      detail: 'Hay una nueva versión lista. Se actualizará cuando no estés usando la app, o puedes recargar ahora.',
      sticky: true,
      closable: true,
      styleClass: 'backdrop-blur-lg rounded-2xl',
    });
  }

  /**
   * Vía primaria de auto-aplicación: al cambiar de ruta el usuario ya está abandonando la pantalla
   * actual, así que es el momento más seguro para recargar. Aplicamos en la URL destino para que el
   * bundle nuevo la resuelva (incluido el caso de una ruta que solo existe en la versión nueva).
   */
  private applyOnNextNavigation(): void {
    if (this.navigationSubscription) {
      return;
    }
    this.navigationSubscription = this.router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e) => {
        if (!this.updateReady() || !this.autoReloadAllowed()) {
          return; // si una pantalla vetó la recarga, esperamos a la siguiente navegación válida
        }
        console.log('SW: Aplicando actualización en cambio de ruta →', e.url);
        this.applyUpdate({ targetUrl: e.url });
      });
  }

  private startInactivityTracker() {
    // Evitar múltiples suscripciones
    if (this.inactivitySubscription) {
      return;
    }

    console.log(`SW: Starting inactivity tracker. Reload in ${INACTIVITY_TIMEOUT_MINUTES} mins of inactivity.`);

    // Corremos fuera de la zona de Angular para no disparar change detection con los eventos
    this.ngZone.runOutsideAngular(() => {
      const activityEvents$ = merge(
        fromEvent(this.document, 'mousemove'),
        fromEvent(this.document, 'keydown'),
        fromEvent(this.document, 'touchstart'),
        fromEvent(this.document, 'scroll')
      );

      this.inactivitySubscription = activityEvents$
        .pipe(
          startWith(null), // Dispara inmediatamente el primer temporizador
          debounceTime(INACTIVITY_TIMEOUT_MINUTES * 60 * 1000)
        )
        .subscribe(() => {
          if (!this.autoReloadAllowed()) {
            return;
          }
          console.log('SW: Inactivity timeout reached. Activating update and reloading.');
          // Volvemos a la zona por si algo toca el estado de Angular durante la recarga.
          this.ngZone.run(() => {
            this.applyUpdate();
          });
        });
    });
  }

  /**
   * Vía MANUAL (botón "Actualizar ahora"): activa la versión nueva y recarga. Por ser una acción
   * explícita del usuario no pasa por la guarda anti-bucle (siempre debe funcionar).
   */
  public activateUpdate() {
    this.swUpdate
      .activateUpdate()
      .then(() => {
        this.forceReload();
      })
      .catch((err) => {
        console.error('Failed to activate update', err);
        this.forceReload();
      });
  }

  /**
   * Si una navegación cayó en el comodín 404, puede que la ruta exista en una versión más nueva
   * (cliente pegado a una versión vieja). Chequeamos y, de haber actualización, activamos + recargamos
   * en la URL intentada para que el bundle nuevo la resuelva. Lo usa el `staleVersionGuard` del `**`.
   *
   * @returns `true` si se disparó una recarga de recuperación; `false` si no hay nada que recuperar
   *   (no es una versión vieja, o ya recargamos hace poco → se muestra el 404 normal).
   */
  public async recoverIfStale(attemptedUrl: string): Promise<boolean> {
    if (!this.swUpdate.isEnabled || this.reloadedRecently()) {
      return false;
    }
    try {
      const hasUpdate = this.updateReady() || (await this.swUpdate.checkForUpdate());
      if (!hasUpdate) {
        return false;
      }
      console.log('SW: Ruta desconocida en versión vieja; recuperando versión nueva para', attemptedUrl);
      this.applyUpdate({ targetUrl: attemptedUrl });
      return true;
    } catch (err) {
      console.error('SW: Error al intentar recuperar versión nueva en 404', err);
      return false;
    }
  }

  /**
   * Aplica la versión nueva por una vía automática y recarga con guarda anti-bucle.
   * `targetUrl`: recarga navegando a esa URL (full load) en vez de recargar la actual.
   */
  private applyUpdate(opts: { targetUrl?: string } = {}): void {
    this.swUpdate
      .activateUpdate()
      .then(() => this.safeReload(opts.targetUrl))
      .catch((err) => {
        console.error('SW: Failed to activate update', err);
        this.safeReload(opts.targetUrl);
      });
  }

  /**
   * Recuperación "carga limpia": desregistra los Service Workers y borra TODAS las cachés del origen,
   * luego recarga. Equivale a lo que ve un dispositivo nuevo (sin SW ni caché): el navegador baja
   * `index.html` + chunks frescos del CDN y vuelve a registrar el SW desde cero.
   *
   * Es la opción "martillo" para estados realmente atascados (`unrecoverable`, instalación corrupta)
   * o como acción manual de soporte. Para una actualización normal NO hace falta (basta
   * `activateUpdate()` + recarga). Tiene su propia guarda anti-bucle, más amplia.
   */
  public async forceCleanReload(): Promise<boolean> {
    if (this.cleanReloadedRecently()) {
      console.warn('SW: Carga limpia reciente; se omite para evitar un bucle.');
      return false;
    }
    // Sin conexión no podemos re-descargar nada: una carga limpia dejaría la app sin assets.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      console.warn('SW: Sin conexión; se pospone la carga limpia.');
      return false;
    }
    try {
      sessionStorage.setItem(CLEAN_RELOAD_FLAG, String(Date.now()));
    } catch {
      /* sessionStorage no disponible: continuamos igual */
    }

    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (err) {
      console.error('SW: Error durante la carga limpia (se recarga igualmente)', err);
    }

    this.doReload();
    return true;
  }

  /** ¿Hubo una recarga automática por actualización dentro de la ventana de guarda? */
  private reloadedRecently(): boolean {
    try {
      const last = Number(sessionStorage.getItem(SW_RELOAD_FLAG)) || 0;
      return Date.now() - last < SW_RELOAD_GUARD_MS;
    } catch {
      return false;
    }
  }

  /** ¿Hubo una carga limpia dentro de su ventana de guarda? (evita bucles de carga limpia). */
  private cleanReloadedRecently(): boolean {
    try {
      const last = Number(sessionStorage.getItem(CLEAN_RELOAD_FLAG)) || 0;
      return Date.now() - last < CLEAN_RELOAD_GUARD_MS;
    } catch {
      return false;
    }
  }

  /** Recarga con guarda anti-bucle en `sessionStorage` (evita recargar en bucle si la activación no resuelve). */
  private safeReload(targetUrl?: string): void {
    if (this.reloadedRecently()) {
      console.warn('SW: Recarga automática reciente; se omite para evitar un bucle.');
      return;
    }
    try {
      sessionStorage.setItem(SW_RELOAD_FLAG, String(Date.now()));
    } catch {
      /* sessionStorage no disponible (modo privado/bloqueado): recargamos igualmente */
    }
    this.doReload(targetUrl);
  }

  /** Recarga efectiva del navegador (encapsulada para poder espiarla en tests). */
  private doReload(targetUrl?: string): void {
    if (targetUrl) {
      window.location.assign(targetUrl);
    } else {
      window.location.reload();
    }
  }

  private forceReload() {
    // El parámetro `forceGet` de reload() está deprecado e ignorado por los navegadores modernos.
    // La versión nueva ya fue activada por `activateUpdate()`, así que una recarga normal basta:
    // el Service Worker sirve los assets de la nueva versión.
    window.location.reload();
  }
}
